import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "motion/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router/dom";
import { ConfirmProvider } from "@/components/ui/confirm";
import { Toaster } from "@/components/ui/toaster";
import { ApiError } from "@/lib/api";
import { meKey } from "@/lib/session";
import { router } from "./routes";
import "./index.css";

// The web fonts, applied without holding up the first paint: until they
// arrive the page shows in the device's own Japanese fonts (display=swap).
const fonts = document.getElementById("webfonts");
if (fonts instanceof HTMLLinkElement) {
  const sheet = document.createElement("link");
  sheet.rel = "stylesheet";
  sheet.href = fonts.href;
  document.head.append(sheet);
}

// A session that expires or loses 2FA mid-use sends the user back through the gate.
const onAuthError = (err: unknown) => {
  if (err instanceof ApiError && (err.status === 401 || err.code === "two_factor_required" || err.code === "password_change_required")) void queryClient.invalidateQueries({ queryKey: meKey });
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: {
    queries: { retry: (n, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && n < 2, refetchOnWindowFocus: false },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ConfirmProvider>
        {/* Every animation honours the device's "reduce motion" setting. */}
        <MotionConfig reducedMotion="user">
          <RouterProvider router={router} />
        </MotionConfig>
      </ConfirmProvider>
      <Toaster />
    </QueryClientProvider>
  </StrictMode>,
);
